import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Laptop, Sprout, Hammer, Languages, ChefHat, HeartHandshake, Truck, PawPrint, Scissors,
  GraduationCap, Hourglass, HandCoins, ShieldCheck, MapPin, Award, ArrowRight, UserPlus, Repeat, Star,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const HERO_IMAGE = 'https://images.unsplash.com/photo-1524247108137-732e0f642303?crop=entropy&cs=srgb&fm=jpg&q=80&w=1920';

const CATEGORIES = [
  { label: 'Informatique', icon: Laptop, q: 'category=digital' },
  { label: 'Jardin', icon: Sprout, q: 'category=jardinage' },
  { label: 'Bricolage', icon: Hammer, q: 'category=bricolage' },
  { label: 'Langues', icon: Languages, q: 'category=education&search=langue' },
  { label: 'Cuisine', icon: ChefHat, q: 'category=cuisine' },
  { label: 'Accompagnement', icon: HeartHandshake, q: 'category=bien-etre' },
  { label: 'Déménagement', icon: Truck, q: 'category=transport' },
  { label: 'Animaux', icon: PawPrint, q: 'category=animaux' },
  { label: 'Couture', icon: Scissors, q: 'category=creatif&search=couture' },
  { label: 'Cours', icon: GraduationCap, q: 'category=education' },
];

const TRUST = [
  { icon: Hourglass, title: '1 heure = 1 heure', text: 'Chaque talent a la même valeur.' },
  { icon: HandCoins, title: 'Sans argent', text: 'On échange du temps, jamais d\u2019euros.' },
  { icon: ShieldCheck, title: 'Membres vérifiés', text: 'E-mail vérifié et avis après chaque échange.' },
  { icon: MapPin, title: '100 % Ottignies-LLN', text: 'Une communauté 100 % locale.' },
];

const STEPS = [
  { icon: UserPlus, title: 'Inscrivez-vous', text: 'Créez votre profil et recevez 10 heures de bienvenue.', img: 'https://images.unsplash.com/photo-1581578017426-04fbc2b0511e?crop=entropy&cs=srgb&fm=jpg&q=80&w=800' },
  { icon: Repeat, title: 'Proposez et demandez', text: 'Publiez ce que vous savez faire et ce dont vous avez besoin.', img: 'https://images.unsplash.com/photo-1555955208-94f6fafea771?crop=entropy&cs=srgb&fm=jpg&q=80&w=800' },
  { icon: Star, title: 'Échangez entre voisins', text: 'Rencontrez-vous, rendez service, gagnez des heures.', img: 'https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&q=80&w=800' },
];

const DISTRICTS = ['Ottignies', 'Louvain-la-Neuve', 'Limelette', 'Céroux-Mousty'];

const cardHover = 'rounded-2xl border border-gray-100 bg-white transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary/40';

export const Hero = () => {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    navigate(`/annonces${q.trim() ? `?search=${encodeURIComponent(q.trim())}` : ''}`);
  };
  return (
    <section className="relative overflow-hidden" data-testid="home-hero">
      <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-white" />
      <div className="relative max-w-6xl mx-auto px-4 pt-16 pb-10 sm:pt-28">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-3xl leading-tight" data-testid="hero-title">
          Échangez vos talents entre voisins à Ottignies-LLN
        </h1>
        <form onSubmit={submit} className="mt-8 flex bg-white rounded-full shadow-xl p-1.5 max-w-2xl" data-testid="hero-search-form">
          <Search className="w-5 h-5 text-gray-400 ml-4 self-center shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="De quoi avez-vous besoin ?"
            className="flex-1 min-w-0 px-3 py-3 bg-transparent outline-none text-gray-900"
            data-testid="hero-search-input"
          />
          <button type="submit" className="rounded-full bg-primary text-white px-5 sm:px-7 font-medium hover:opacity-90 transition-opacity" data-testid="hero-search-btn">
            Chercher
          </button>
        </form>
        <div className="mt-10 flex flex-wrap gap-2 sm:gap-3 max-w-4xl" data-testid="hero-categories">
          {CATEGORIES.map(({ label, icon: Icon, q: query }) => (
            <Link key={label} to={`/annonces?${query}`} className={`${cardHover} inline-flex items-center gap-2 px-4 py-2.5 !rounded-full`} data-testid={`hero-cat-${label.toLowerCase()}`}>
              <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{label}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const avatarSrc = (url) => (url && url.startsWith('/api') ? `${BACKEND}${url}` : url);

export const Founders = () => {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND}/api/members/founders`).then((r) => setMembers(r.data)).catch(() => setMembers([]));
  }, []);
  if (members.length < 3) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 py-16" data-testid="founders-section">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">Nos membres fondateurs</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {members.map((m) => (
          <Link key={m.id} to={`/users/${m.id}`} className={`${cardHover} p-5 flex flex-col items-center text-center`} data-testid={`founder-card-${m.id}`}>
            {m.avatar_url ? (
              <img src={avatarSrc(m.avatar_url)} alt={m.first_name} className="w-20 h-20 rounded-full object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-primary/10 text-primary flex items-center justify-center text-2xl font-bold">{m.first_name[0]}</div>
            )}
            <p className="mt-3 font-semibold text-gray-900">{m.first_name}</p>
            <p className="text-sm text-gray-600 line-clamp-1">{m.main_skill || m.main_category || 'Talents variés'}</p>
            {m.location && <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{m.location}</p>}
            <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs font-medium px-3 py-1">
              <Award className="w-3 h-3" /> Membre fondateur
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
};

export const TrustBand = () => (
  <section className="bg-primary/5 border-y border-primary/10" data-testid="trust-band">
    <div className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
      {TRUST.map(({ icon: Icon, title, text }) => (
        <div key={title} className="flex flex-col sm:flex-row items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-sm shrink-0">
            <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{title}</p>
            <p className="text-sm text-gray-600">{text}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

export const HowItWorks = () => (
  <section className="max-w-6xl mx-auto px-4 py-16" data-testid="how-it-works-section">
    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">Comment ça marche</h2>
    <div className="grid md:grid-cols-3 gap-6">
      {STEPS.map(({ icon: Icon, title, text, img }, i) => (
        <div key={title} className={`${cardHover} overflow-hidden`} data-testid={`step-${i + 1}`}>
          <img src={img} alt={title} className="w-full h-44 object-cover" />
          <div className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">{i + 1}</span>
              <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <p className="font-semibold text-gray-900">{title}</p>
            <p className="text-sm text-gray-600 mt-1">{text}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

export const NearYou = () => (
  <section className="max-w-6xl mx-auto px-4 pb-20" data-testid="near-you-section">
    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">Près de chez vous</h2>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {DISTRICTS.map((d) => (
        <Link key={d} to={`/annonces?location=${encodeURIComponent(d)}`} className={`${cardHover} group p-6`} data-testid={`district-${d.toLowerCase()}`}>
          <MapPin className="w-6 h-6 text-primary mb-4" strokeWidth={1.5} />
          <p className="font-semibold text-gray-900">{d}</p>
          <p className="text-sm text-primary mt-2 flex items-center gap-1">
            Voir les offres <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </p>
        </Link>
      ))}
    </div>
  </section>
);
